import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Add20Regular,
  Search20Regular,
  Person24Regular,
  Edit20Regular,
  ArrowLeft24Regular,
} from "@fluentui/react-icons";
import { PageHeader } from "../../components/PageHeader";
import {
  ApiError,
  getWorkerById,
  getWorkers,
  type Worker as ApiWorker,
} from "../../trabalhadores/trabalhadoresApi";

interface TrabalhadorView {
  id: number;
  nome: string;
  codigo?: string | null;
  funcao?: string | null;
  ativo: boolean;
  custoHora?: number | null;
}

const formatOptional = (value?: string | number | null): string => {
  if (value === undefined || value === null) return "—";
  if (typeof value === "string") return value.trim() ? value : "—";
  return String(value);
};

const formatCurrency = (value?: number | null): string => {
  if (value === undefined || value === null) return "—";
  return value.toLocaleString("pt-PT", { style: "currency", currency: "EUR" });
};

const parseErrorMessage = (error: unknown): string => {
  if (error instanceof ApiError) {
    if (error.status === 401) {
      return "Sessão expirada. Por favor, autentique-se novamente.";
    }
    if (error.status === 403) {
      return "Sem permissões para consultar trabalhadores.";
    }
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return "Não foi possível carregar os trabalhadores.";
};

const mapWorkerToView = (worker: ApiWorker): TrabalhadorView => {
  return {
    id: worker.id,
    nome: worker.name,
    codigo: worker.code ?? null,
    funcao: worker.role ?? null,
    ativo: worker.isActive,
    custoHora: worker.hourlyCost ?? null,
  };
};

export function TrabalhadoresView() {
  const navigate = useNavigate();
  const [trabalhadores, setTrabalhadores] = useState<TrabalhadorView[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selectedWorkerId, setSelectedWorkerId] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [isMobileDetailOpen, setIsMobileDetailOpen] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const loadWorkers = async () => {
      setIsLoading(true);
      setLoadError(null);

      try {
        const response = await getWorkers();
        if (!isMounted) return;

        const mapped = response.map(mapWorkerToView);
        setTrabalhadores(mapped);
        setSelectedWorkerId((current) => {
          if (current && mapped.some((item) => item.id === current)) {
            return current;
          }
          return mapped[0]?.id ?? null;
        });
      } catch (error) {
        if (!isMounted) return;
        setTrabalhadores([]);
        setSelectedWorkerId(null);
        setLoadError(parseErrorMessage(error));
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadWorkers();

    return () => {
      isMounted = false;
    };
  }, []);

  const filteredTrabalhadores = useMemo(
    () =>
      trabalhadores.filter((trabalhador) => {
        const matchesSearch =
          trabalhador.nome
            .toLowerCase()
            .includes(searchQuery.toLowerCase()) ||
          (trabalhador.codigo ?? "")
            .toLowerCase()
            .includes(searchQuery.toLowerCase()) ||
          (trabalhador.funcao ?? "")
            .toLowerCase()
            .includes(searchQuery.toLowerCase());
        return matchesSearch;
      }),
    [trabalhadores, searchQuery],
  );

  useEffect(() => {
    if (isLoading) return;

    if (filteredTrabalhadores.length === 0) {
      setSelectedWorkerId(null);
      return;
    }

    const hasSelection = filteredTrabalhadores.some(
      (entry) => entry.id === selectedWorkerId,
    );
    if (!hasSelection) {
      setSelectedWorkerId(filteredTrabalhadores[0].id);
    }
  }, [filteredTrabalhadores, isLoading, selectedWorkerId]);

  useEffect(() => {
    if (!selectedWorkerId) return;

    let isMounted = true;

    const loadSelectedWorker = async () => {
      try {
        const response = await getWorkerById(selectedWorkerId);
        if (!isMounted) return;

        const mapped = mapWorkerToView(response);
        setTrabalhadores((current) =>
          current.map((trabalhador) =>
            trabalhador.id === selectedWorkerId ? mapped : trabalhador,
          ),
        );
      } catch {
        // Keep list data if detail fetch fails.
      }
    };

    loadSelectedWorker();

    return () => {
      isMounted = false;
    };
  }, [selectedWorkerId]);

  const selectedTrabalhador = filteredTrabalhadores.find(
    (e) => e.id === selectedWorkerId,
  );

  const handleWorkerSelect = (workerId: number) => {
    setSelectedWorkerId(workerId);
    setIsMobileDetailOpen(true);
  };

  const handleMobileBack = () => {
    setIsMobileDetailOpen(false);
  };

  const actions = (
    <>
      <button
        className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded transition-colors flex items-center gap-2 min-h-[44px]"
        style={{
          borderRadius: "var(--radius-button)",
          height: "var(--button-height)",
        }}
        onClick={() => navigate("/trabalhadores/novo")}
      >
        <Add20Regular className="w-5 h-5" />
        Novo Trabalhador
      </button>
    </>
  );

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        title="Trabalhadores"
        subtitle="Lista de trabalhadores e respetivos custos"
        actions={actions}
      />

      <div className="flex-1 flex overflow-hidden">
        <div
          className={`${
            isMobileDetailOpen ? "hidden md:flex" : "flex"
          } flex-col w-full md:w-80 lg:w-96 border-r border-border bg-muted/30`}
        >
          <div className="p-4 border-b border-border bg-card">
            <div className="relative">
              <Search20Regular className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-secondary-text" />
              <input
                type="text"
                placeholder="Procurar trabalhadores..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20"
                style={{ borderRadius: "var(--radius-button)" }}
              />
            </div>

            <div className="text-sm text-[var(--muted-foreground)] mt-3">
              {isLoading
                ? "A carregar trabalhadores..."
                : `${filteredTrabalhadores.length} trabalhador${
                    filteredTrabalhadores.length !== 1 ? "es" : ""
                  } encontrado${
                    filteredTrabalhadores.length !== 1 ? "s" : ""
                  }`}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto">
            {isLoading && (
              <div className="flex flex-col items-center justify-center py-12 px-4">
                <Person24Regular className="text-[var(--muted-foreground)] mb-4" />
                <p className="text-[var(--muted-foreground)] text-center">
                  A carregar trabalhadores...
                </p>
              </div>
            )}

            {!isLoading && loadError && (
              <div className="flex flex-col items-center justify-center py-12 px-4">
                <Person24Regular className="text-[var(--muted-foreground)] mb-4" />
                <p className="text-[var(--destructive)] text-center">
                  {loadError}
                </p>
              </div>
            )}

            {!isLoading &&
              !loadError &&
              filteredTrabalhadores.map((trabalhador) => (
                <button
                  key={trabalhador.id}
                  onClick={() => handleWorkerSelect(trabalhador.id)}
                  className={`w-full text-left p-4 border-b border-border transition-colors relative ${
                    selectedWorkerId === trabalhador.id ? "bg-accent/50" : ""
                  }`}
                  style={{
                    backgroundColor:
                      selectedWorkerId === trabalhador.id
                        ? "var(--accent)"
                        : "var(--card)",
                  }}
                  onMouseEnter={(e) => {
                    if (selectedWorkerId !== trabalhador.id) {
                      e.currentTarget.style.backgroundColor = "var(--accent)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (selectedWorkerId !== trabalhador.id) {
                      e.currentTarget.style.backgroundColor = "var(--card)";
                    }
                  }}
                >
                  {selectedWorkerId === trabalhador.id && (
                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-primary" />
                  )}

                  <div className="flex items-start gap-3 pl-2">
                    <div className="flex-1 min-w-0">
                      <p
                        className="truncate"
                        style={{ fontWeight: "var(--font-weight-semi-bold)" }}
                      >
                        {trabalhador.nome}
                      </p>

                      <p
                        className="text-secondary-text mt-0.5"
                        style={{ fontSize: "var(--text-caption)" }}
                      >
                        {formatOptional(trabalhador.funcao)}
                      </p>

                      <p
                        className="text-secondary-text mt-1"
                        style={{ fontSize: "var(--text-caption)" }}
                      >
                        {trabalhador.ativo ? "Ativo" : "Inativo"}
                      </p>
                    </div>
                  </div>
                </button>
              ))}

            {!isLoading && !loadError && filteredTrabalhadores.length === 0 && (
              <div className="p-8 text-center text-secondary-text">
                <p>Nenhum trabalhador encontrado</p>
              </div>
            )}
          </div>
        </div>

        <div
          className={`${
            isMobileDetailOpen ? "flex" : "hidden md:flex"
          } flex-1 flex-col overflow-y-auto bg-background`}
        >
          {!selectedTrabalhador ? (
            <div className="flex-1 flex items-center justify-center p-8">
              <div className="text-center max-w-md">
                <Person24Regular className="w-16 h-16 mx-auto text-secondary-text mb-4" />
                <p
                  className="text-secondary-text"
                  style={{ fontSize: "var(--text-base)" }}
                >
                  Selecione um trabalhador para ver os detalhes
                </p>
              </div>
            </div>
          ) : (
            <div className="p-4 md:p-6 space-y-6">
              <button
                onClick={handleMobileBack}
                className="md:hidden flex items-center gap-2 text-secondary-text hover:text-foreground transition-colors mb-4"
              >
                <ArrowLeft24Regular className="w-6 h-6" />
                <span>Voltar</span>
              </button>

              <div
                className="bg-card border border-border p-6"
                style={{ borderRadius: "var(--radius-card)" }}
              >
                <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div>
                      <h3
                        className="text-foreground"
                        style={{
                          fontSize: "var(--text-large)",
                          fontWeight: "var(--font-weight-semi-bold)",
                          lineHeight: "1.3",
                        }}
                      >
                        {selectedTrabalhador.nome}
                      </h3>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <button
                      className="px-4 py-2 bg-secondary hover:bg-accent border border-border rounded transition-colors flex items-center gap-2 min-h-[44px]"
                      style={{ borderRadius: "var(--radius-button)" }}
                      onClick={() =>
                        navigate(`/trabalhadores/editar/${selectedTrabalhador.id}`)
                      }
                    >
                      <Edit20Regular className="w-5 h-5" />
                      <span>Editar Trabalhador</span>
                    </button>
                  </div>
                </div>
              </div>

              <div
                className="bg-card border border-border p-6 space-y-4"
                style={{ borderRadius: "var(--radius-card)" }}
              >
                <h4
                  className="text-foreground"
                  style={{
                    fontSize: "var(--text-section-title)",
                    fontWeight: "var(--font-weight-semi-bold)",
                  }}
                >
                  Dados do Trabalhador
                </h4>

                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <p
                      className="text-secondary-text"
                      style={{ fontSize: "var(--text-caption)" }}
                    >
                      Identificação Interna
                    </p>
                    <p className="mt-0.5">
                      {formatOptional(selectedTrabalhador.codigo)}
                    </p>
                  </div>

                  <div>
                    <p
                      className="text-secondary-text"
                      style={{ fontSize: "var(--text-caption)" }}
                    >
                      Função / Categoria
                    </p>
                    <p className="mt-0.5">
                      {formatOptional(selectedTrabalhador.funcao)}
                    </p>
                  </div>

                  <div>
                    <p
                      className="text-secondary-text"
                      style={{ fontSize: "var(--text-caption)" }}
                    >
                      Estado
                    </p>
                    <p className="mt-0.5">
                      {selectedTrabalhador.ativo ? "Ativo" : "Inativo"}
                    </p>
                  </div>

                  <div>
                    <p
                      className="text-secondary-text"
                      style={{ fontSize: "var(--text-caption)" }}
                    >
                      Custo / Hora
                    </p>
                    <p className="mt-0.5">
                      {formatCurrency(selectedTrabalhador.custoHora)}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
